import { useMemo } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import { logError } from "@entrilia/shared/logging";
import type { ValidationResult } from "@entrilia/shared/utilities/validators";
import { invalidResult, validResult } from "@entrilia/shared/utilities/validators";
import adminApi from "../../api/adminApi";
import type {
  BusinessCentralObjectType,
  Field,
  FieldIdToLookupReferences,
  FieldIdToValueMap,
  FieldsCitationsMap,
  FieldsConfidenceMap,
  LookupObjectReference,
  ObjectTypeToLookupReferences,
  PageLayout,
} from "../../api/types/objectTypes";
import FieldList from "./FieldList";
import type { EmailFieldEditMode, FieldValuesState } from "./FieldValuesContext";
import { FieldValuesContextProvider } from "./FieldValuesContextProvider";
import { getUpdatedFieldValues, getVisibleFieldGroupModels } from "./fieldValuesManagement";

const validateUniqueField = withErrorHandling(adminApi.validateUniqueField);

interface Props {
  objectType: string;
  businessCentralType: BusinessCentralObjectType | undefined;
  fields: Field[];
  cardLayout: PageLayout;
  initialFieldValues: FieldIdToValueMap;
  fieldsConfidence?: FieldsConfidenceMap;
  fieldsCitations?: FieldsCitationsMap;
  lookupObjects?: FieldIdToLookupReferences;
  lookupOptionsByObjectType?: ObjectTypeToLookupReferences;
  onAddLookupOption: (objectType: string, lookupObjectReference: LookupObjectReference) => void;
  allowEditing: boolean;
  fieldValuesState: FieldValuesState;
  onFieldValuesStateChange: (stateUpdate: Partial<FieldValuesState>) => void;
  emailFieldEditMode?: EmailFieldEditMode;
  showFieldValueBorders?: boolean;
  fieldsAutoFillTooltips?: Record<string, string>;
}

const FieldValuesContainer = ({
  objectType,
  businessCentralType,
  fields,
  cardLayout,
  initialFieldValues,
  lookupObjects,
  lookupOptionsByObjectType,
  onAddLookupOption,
  fieldsConfidence,
  fieldsCitations,
  allowEditing,
  fieldValuesState,
  emailFieldEditMode,
  onFieldValuesStateChange,
  showFieldValueBorders,
  fieldsAutoFillTooltips,
}: Props) => {
  const fieldGroups = useMemo(
    () => getVisibleFieldGroupModels(cardLayout.fieldGroups, !allowEditing, fields),
    [cardLayout.fieldGroups, allowEditing, fields]
  );

  const handleStateValidation = (validationResult: ValidationResult, fieldValues: FieldIdToValueMap) => {
    if (validationResult.isValid) {
      onFieldValuesStateChange({ fieldValues, isValid: true, fieldEditState: "viewing" });
    } else {
      onFieldValuesStateChange({ fieldValues, isValid: false, fieldEditState: "editing" });
    }
  };

  const onFieldValueChange = (value: unknown, fieldId: string, validationResult: ValidationResult) => {
    const changedFieldValue = fieldValuesState.fieldValues[fieldId];

    // If field value is not defined or reset to undefined - no need to update
    if (!changedFieldValue && !value) {
      handleStateValidation(validationResult, fieldValuesState.fieldValues);
      return;
    }

    const updatedFieldValues = getUpdatedFieldValues(structuredClone(fieldValuesState.fieldValues), value, fieldId);

    if (fieldValuesState.fieldEditState === "canceling") {
      onFieldValuesStateChange({
        fieldValues: structuredClone(initialFieldValues),
        isValid: true,
        fieldEditState: "viewing",
      });
      return;
    }

    handleStateValidation(validationResult, updatedFieldValues);
  };

  const validateUniqueFieldValue = async (fieldId: string, value: unknown): Promise<ValidationResult> => {
    const isUniqueValidationSupported = businessCentralType !== "Entity";
    if (!isUniqueValidationSupported) {
      return validResult();
    }

    const storedFieldValue = fieldValuesState.fieldValues[fieldId];
    if (storedFieldValue === value) {
      return validResult();
    }

    const [resp, error] = await validateUniqueField(objectType, { fieldId, value });
    if (error) {
      logError(error, `[FieldValuesContainer] validateUniqueField (${objectType} - ${fieldId})`);
      return validResult();
    }

    return resp.result ? validResult() : invalidResult("Duplicate value in a unique field");
  };

  return (
    <FieldValuesContextProvider
      objectType={objectType}
      fieldValuesState={fieldValuesState}
      updateFieldValuesState={onFieldValuesStateChange}
      fieldGroups={fieldGroups}
      lookupObjects={lookupObjects ?? {}}
      lookupOptionsByObjectType={lookupOptionsByObjectType ?? {}}
      addLookupOption={onAddLookupOption}
      fieldsConfidence={fieldsConfidence}
      fieldsCitations={fieldsCitations}
      onFieldValueChange={onFieldValueChange}
      validateUniqueFieldValue={validateUniqueFieldValue}
      emailFieldEditMode={emailFieldEditMode}
      showFieldValueBorders={showFieldValueBorders}
      fieldsAutoFillTooltips={fieldsAutoFillTooltips}
    >
      <FieldList />
    </FieldValuesContextProvider>
  );
};

export default FieldValuesContainer;
